'use client'

// Roadmap = kolom per PERIODE (Telat / Bulan Ini / Bulan Depan / Nanti / Tanpa Target / Selesai).
// Tiap project = kartu di kolom sesuai target selesai. Beda jelas dari Timeline (yang sumbu-tanggal).
import { ExternalLink } from 'lucide-react'
import { projectProgress, isoDay } from '@/lib/dev-sprint'
import { useToast } from '@/context/ToastContext'
import type { BoardData, Filters, DevDerived } from './types'

const fmt = (iso: string | null) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')
const ym = (iso: string) => iso.slice(0, 7)

type BucketKey = 'overdue' | 'thismonth' | 'upcoming' | 'notarget' | 'done'
const BUCKETS: { key: BucketKey; label: string; bg: string; text: string }[] = [
  { key: 'overdue', label: 'Telat', bg: '#FCE8E8', text: '#C0362C' },
  { key: 'thismonth', label: 'Bulan Ini', bg: '#FFF4DC', text: '#9A6B00' },
  { key: 'upcoming', label: 'Mendatang', bg: '#E7EEFF', text: '#3A5BB8' },
  { key: 'notarget', label: 'Tanpa Target', bg: '#F1F1EF', text: '#8B8A85' },
  { key: 'done', label: 'Selesai', bg: '#E4F5EA', text: '#1E7A46' },
]

interface Props {
  board: BoardData
  derived: DevDerived
  filters: Filters
  today: string
  isLead: boolean
  onOpenProject: (id: string) => void
  onChanged: () => void
}

export function RoadmapView({ board, derived, filters, today, isLead, onOpenProject, onChanged }: Props) {
  const { statusOf } = derived
  const { toast } = useToast()

  const projects = filters.project ? board.projects.filter((p) => p.id === filters.project) : board.projects

  const saveTarget = async (id: string, value: string) => {
    try {
      const res = await fetch(`/api/dev/projects/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ deadline: value || null }) })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Gagal')
      toast('Target diperbarui', 'success')
      onChanged()
    } catch (e) { toast(e instanceof Error ? e.message : 'Gagal', 'error') }
  }

  const curYm = ym(today)
  const bucketOf = (pr: ReturnType<typeof projectProgress>): BucketKey => {
    if (pr.pct >= 100) return 'done'
    if (!pr.finish) return 'notarget'
    if (pr.finish < today) return 'overdue'
    if (ym(pr.finish) === curYm) return 'thismonth'
    return 'upcoming'
  }

  const items = projects.map((p) => {
    const pr = projectProgress(p, board.tasks.filter((t) => t.projectId === p.id), statusOf, today)
    return { p, pr, bucket: bucketOf(pr) }
  })
  const byBucket = (k: BucketKey) =>
    items.filter((it) => it.bucket === k).sort((a, b) => (isoDay(a.pr.finish) || '9999').localeCompare(isoDay(b.pr.finish) || '9999'))

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-[13px] font-bold text-[#1A1916]">Roadmap Project</h3>
        <span className="text-[11px] text-[#AEADA8]">project dikelompokkan per target selesai{isLead ? ' · ubah target lewat tanggal di kartu' : ''}</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {BUCKETS.map((b) => {
          const list = byBucket(b.key)
          return (
            <div key={b.key} className="flex-shrink-0 w-[268px]">
              <div className="flex items-center gap-2 px-1 mb-2">
                <span className="text-[12px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: b.bg, color: b.text }}>{b.label}</span>
                <span className="text-[11px] text-[#AEADA8] tabular-nums">{list.length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {list.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-[#E7E5DF] py-6 text-center text-[11px] text-[#C9C7C0]">—</div>
                ) : (
                  list.map(({ p, pr }) => (
                    <div key={p.id} className="rounded-2xl border border-[#E7E5DF] bg-white p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                        <button onClick={() => onOpenProject(p.id)} className="text-[13px] font-semibold text-[#1A1916] truncate text-left hover:underline flex-1" title={p.name}>{p.name}</button>
                        <a href={`/projects/${p.id}`} target="_blank" rel="noreferrer" className="text-[#C9C7C0] hover:text-[#1A1916]" title="Buka project"><ExternalLink className="w-3.5 h-3.5" /></a>
                      </div>

                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="flex-1 h-1.5 rounded-full bg-[#F0EEE7] overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pr.pct}%`, backgroundColor: p.color }} />
                        </div>
                        <span className="text-[12px] font-bold text-[#1A1916] tabular-nums w-8 text-right">{pr.pct}%</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-[#8B8A85] tabular-nums mb-2">
                        <span>{pr.tasksDone}/{pr.tasksTotal} task</span>
                        {pr.onPace === false && <span className="font-semibold text-[#C0362C]">· behind</span>}
                        {pr.onPace === true && <span className="font-semibold text-[#1E7A46]">· on pace</span>}
                      </div>

                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[#F2F1EC]">
                        <div>
                          <div className="text-[10px] text-[#AEADA8] mb-0.5">Mulai</div>
                          <div className="text-[11px] text-[#4A4844] tabular-nums">{fmt(pr.start)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#AEADA8] mb-0.5">Target</div>
                          {isLead ? (
                            <input
                              type="date"
                              defaultValue={isoDay(p.deadline)}
                              onChange={(e) => saveTarget(p.id, e.target.value)}
                              className="w-full bg-[#F7F6F2] border border-[#E7E5DF] rounded-md px-1.5 py-1 text-[11px] text-[#1A1916] tabular-nums focus:outline-none focus:ring-2 focus:ring-[#D4A843]/40"
                            />
                          ) : (
                            <div className="text-[11px] text-[#4A4844] tabular-nums">{fmt(pr.finish)}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
